import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateReceiptForBusiness } from "apis";
import { getAllQueuesQuery } from "./get-all-queues";
import { getAppointmentsQueryName } from "./get-appointments";
import { RECEIPT_TYPE, TCreateReceiptBody } from "@elixir/types";
import getB from "../../utils/bridges/EkaBridge";
import { useMassageReceiptDataWithConfigForPDF } from "./receipt-v2/use-massage-receipt-data-and-config-for-pdf";
import { makeReceiptOnLocal } from "./receipt-v2/should-receipt-be-made-on-local-util";
import { track } from "../../utils/tracking";
import { getPatientDetails } from "../../utils/bridges/methods";

const usePutUpdateReceipt = () => {
  const queryClient = useQueryClient();
  const massageReceiptDataWithConfigForPDF =
    useMassageReceiptDataWithConfigForPDF();

  return useMutation(
    async ({
      businessId,
      receiptId,
      payload,
      meta,
      abhaId,
    }: {
      businessId: string;
      receiptId: number;
      payload: TCreateReceiptBody["payload"];
      meta: TCreateReceiptBody["meta"];
      abhaId?: string;
    }) => {
      const shouldGeneratePDF = makeReceiptOnLocal();

      // Get abhaId from bridge if not provided
      let finalAbhaId = abhaId;
      if (!finalAbhaId && meta.patient_oid) {
        const patients = await getPatientDetails({ patientOids: [meta.patient_oid] });
        finalAbhaId = patients?.[0]?.patient?.abha?.id;
      }

      return updateReceiptForBusiness({
        businessId,
        receiptId,
        payload,
        meta,
        shouldGeneratePDF,
        abhaId: finalAbhaId,
      });
    },
    {
      onSuccess: async (response, { payload, meta }) => {
        queryClient.invalidateQueries(getAppointmentsQueryName());
        const toMakeReceiptOnLocal = makeReceiptOnLocal();
        if (toMakeReceiptOnLocal) {
          massageReceiptDataWithConfigForPDF(response);
        }
        track("UPDATE_RECEIPT_V3", {
          isMadeOnLocal: String(toMakeReceiptOnLocal),
          receiptID: String(response?.data?.receipt_id || ""),
          aid: String(response?.data?.aid || ""),
        });

        if (
          payload.amount_due ||
          meta.receipt_type === RECEIPT_TYPE.DUE_PAYMENT
        ) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await getB().fetchPatientListFromServer([meta.patient_oid]);
          queryClient.invalidateQueries(getAllQueuesQuery({}));
        }
      },
    }
  );
};

export default usePutUpdateReceipt;
